/**
 * Team Inbox — the 24h session banner (UI Spec §7.3 "the heartbeat of the
 * screen"): green "● Session open · closes in 23h 41m" → amber under 2h →
 * amber "⏳ Outside 24h — templates only". Pinned above the composer.
 *
 * The countdown is LIVE: computed client-side from the thread's last
 * inbound timestamp (lib/sessionWindow.ts pure math) and re-rendered
 * every minute + exactly at the close instant, so a session expiring
 * mid-view flips the banner (and the composer above it) without a reload.
 */
import { useEffect, useState } from 'react';
import { nextTickMs, sessionState } from '../lib/sessionWindow';
import type { SessionState } from '../lib/types';

/** Live session state for a thread — shared by banner + composer. */
export function useSessionState(lastInboundMs: number | null): SessionState {
  const [state, setState] = useState<SessionState>(() =>
    sessionState(lastInboundMs, Date.now()),
  );
  useEffect(() => {
    let timer: number | null = null;
    function tick() {
      const now = Date.now();
      const s = sessionState(lastInboundMs, now);
      setState(s);
      const next = nextTickMs(s, now);
      if (next != null) timer = window.setTimeout(tick, next);
    }
    tick();
    return () => {
      if (timer != null) window.clearTimeout(timer);
    };
  }, [lastInboundMs]);
  return state;
}

export function SessionBanner({ state }: { state: SessionState }) {
  return (
    <div className={`ti-session ${state.tone}`} role="status" aria-live="polite">
      {state.label}
    </div>
  );
}
